<template>
  
  <app-dlg
      :show-dlg.sync="shownRelay"
      title="Live View"
      :default-padding="false"
      :full-screen="true"
  >
    
    <template #header>
      <v-toolbar flat dense>
        <v-toolbar-title>Live View</v-toolbar-title>
        
        <!-- Employee and Device Info -->
        <div class="mx-3 d-flex align-center" v-if="employee || device">
          <v-icon small class="mr-1">mdi-account</v-icon>
          <span class="mr-3">{{ employee && employee.name || 'Unassigned' }}</span>
          <v-icon small class="mr-1">mdi-desktop-classic</v-icon>
          <span>{{ device && (device.name || device.hostname) || 'Unknown' }}</span>
        </div>
        
        <v-spacer></v-spacer>
        
        <!-- Monitor Selection -->
        <v-select
            v-model="monitor"
            :items="monitorItems"
            item-text="text"
            item-value="value"
            outlined
            dense
            hide-details
            class="mx-2"
            style="max-width: 200px"
            @change="handleMonitorChange"
        >
        </v-select>
        
        <!-- Quality Slider -->
        <v-chip class="mx-2" outlined>
          <v-icon left small>mdi-quality-high</v-icon>
          Quality
        </v-chip>
        <v-slider
            v-model="quality"
            :min="0.1"
            :max="1"
            :step="0.1"
            dense
            hide-details
            thumb-label
            class="mx-2"
            style="max-width: 150px"
            @change="handleMonitorChange"
        >
          <template v-slot:thumb-label="{ value }">
            {{ Math.round(value * 100) }}%
          </template>
        </v-slider>
        
        <!-- FPS Selection -->
        <v-select
            v-model="fps"
            :items="[0.5, 1, 2, 3, 5, 10]"
            outlined
            dense
            hide-details
            class="mx-2"
            style="max-width: 120px"
            suffix="sec"
            @change="handleMonitorChange"
        >
          <template v-slot:selection="{ item }">
            {{ item }}s
          </template>
          <template v-slot:item="{ item }">
            {{ item }} sec
          </template>
        </v-select>
        
        <v-divider vertical class="mx-2"></v-divider>
        
        <!-- View Controls -->
        <v-btn-toggle v-model="fitMode" mandatory dense class="mr-2">
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-btn small value="contain" v-on="on">
                <v-icon small>mdi-fit-to-screen-outline</v-icon>
              </v-btn>
            </template>
            <span>Fit to screen (1)</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-btn small value="cover" v-on="on">
                <v-icon small>mdi-fit-to-screen</v-icon>
              </v-btn>
            </template>
            <span>Fill screen (2)</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-btn small value="actual" v-on="on">
                <v-icon small>mdi-image-size-select-actual</v-icon>
              </v-btn>
            </template>
            <span>Actual size (3)</span>
          </v-tooltip>
        </v-btn-toggle>
        
        <!-- Zoom Controls -->
        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <v-btn icon small @click="zoomOut" :disabled="zoom <= 0.5" v-on="on">
              <v-icon>mdi-magnify-minus</v-icon>
            </v-btn>
          </template>
          <span>Zoom out (-)</span>
        </v-tooltip>
        <v-chip small class="mx-1">{{ Math.round(zoom * 100) }}%</v-chip>
        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <v-btn icon small @click="zoomIn" :disabled="zoom >= 3" v-on="on">
              <v-icon>mdi-magnify-plus</v-icon>
            </v-btn>
          </template>
          <span>Zoom in (+)</span>
        </v-tooltip>
        
        <!-- Rotate Control -->
        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <v-btn icon small @click="rotate" class="mx-1" v-on="on">
              <v-icon>mdi-rotate-right</v-icon>
            </v-btn>
          </template>
          <span>Rotate 90° (R)</span>
        </v-tooltip>
        
        <!-- Capture Button -->
        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <v-btn icon small @click="captureImage" class="ml-2" color="primary" v-on="on">
              <v-icon>mdi-camera</v-icon>
            </v-btn>
          </template>
          <span>Capture screenshot (C)</span>
        </v-tooltip>
      </v-toolbar>
    </template>
    
    <div class="fill-height of-y">
      <div
          v-if="loading"
          class="fill-height d-flex justify-center align-center pa-5"
      >
        <v-progress-circular
            :size="50"
            color="primary"
            indeterminate
        ></v-progress-circular>
      </div>
      <div 
          class="image-container d-flex align-center justify-center" 
          style="height: 100%; position: relative; overflow: auto;"
          ref="imageContainer"
          @mousedown="startPan"
          @mousemove="doPan"
          @mouseup="endPan"
          @mouseleave="endPan"
          @wheel="handleWheel"
          :style="containerStyle"
      >
        <div
            v-if="imageData"
            :style="imageWrapperStyle"
            class="image-wrapper"
        >
          <img 
              :src="imageData" 
              alt="Live view stream" 
              :style="imageStyle"
              ref="liveImage"
              @load="onImageLoad"
              @dragstart.prevent
          >
        </div>
        <div v-else class="d-flex align-center justify-center fill-height">
          <v-icon size="64" color="grey">mdi-image-off</v-icon>
        </div>
      </div>
    </div>
    
    <template v-slot:actions>
      <v-btn
          @click="shownRelay = false"
          small
          class="ml-2"
          color="secondary"
          variant="contained"
          size="small"
      >
        Done
      </v-btn>
    </template>
  </app-dlg>

</template>

<script>


import AppDlg from "@/components/Shared/AppDlg.vue";
import AppList from "@/components/Shared/AppList/AppList.vue";
import {getSocket} from "@/core/realtimeSync";
import {mapGetters} from "vuex";

export default {
  components: {AppList, AppDlg},
  props: {
    deviceId: {
      type: String,
      required: true
    },
  },
  created() {
    // Add keyboard shortcuts
    this.handleKeyboard = (e) => {
      if (!this.deviceId) return;
      
      // Zoom controls
      if (e.key === '+' || e.key === '=') {
        e.preventDefault();
        this.zoomIn();
      } else if (e.key === '-' || e.key === '_') {
        e.preventDefault();
        this.zoomOut();
      }
      // Rotate with R key
      else if (e.key === 'r' || e.key === 'R') {
        e.preventDefault();
        this.rotate();
      }
      // Capture with C or Space
      else if (e.key === 'c' || e.key === 'C' || e.key === ' ') {
        e.preventDefault();
        this.captureImage();
      }
      // Fit modes with number keys
      else if (e.key === '1') {
        e.preventDefault();
        this.fitMode = 'contain';
        this.zoom = 1;
      } else if (e.key === '2') {
        e.preventDefault();
        this.fitMode = 'cover';
        this.zoom = 1;
      } else if (e.key === '3') {
        e.preventDefault();
        this.fitMode = 'actual';
      }
      // Reset with 0
      else if (e.key === '0') {
        e.preventDefault();
        this.zoom = 1;
        this.rotation = 0;
        this.fitMode = 'contain';
        this.resetPan();
      }
    };
    
    window.addEventListener('keydown', this.handleKeyboard);
  },
  
  async beforeDestroy() {
    window.removeEventListener('keydown', this.handleKeyboard);
    await this.handleClose();
  },
  data() {
    return {
      loading: true,
      sessionId: null,
      fps: null,
      monitor: null,
      quality: null,
      tte: null,
      socket: getSocket(),
      imageData: null,
      monitorCount: 1,
      selectedMonitor: null,
      keepAliveInterval: null,
      // View controls
      zoom: 1,
      rotation: 0,
      fitMode: 'contain', // 'contain', 'cover', 'actual'
      imageDimensions: { width: 0, height: 0 },
      // Pan/drag state
      isPanning: false,
      panStart: { x: 0, y: 0 },
      panOffset: { x: 0, y: 0 },
    }
  },
  watch: {
    deviceId: {
      immediate: true,
      handler(val) {
        if (this.deviceId) this.init()
        
      }
    },
    sessionId: {
      immediate: true,
      handler(val) {
        if (val) {
          this.handleLiveViewStart()
        }
      }
    },
    fitMode() {
      // Reset pan when changing fit modes
      this.resetPan();
    }
  },
  methods: {
    async init() {
      try {
        this.loading = true
        const res = await this.$newReq('POST', `devices/liveViewInit`, {deviceId: this.deviceId})
        this.sessionId = res.sessionId
        this.fps = res.fps
        this.monitor = res.monitor
        this.quality = res.quality
        this.tte = res.tte
        
      } catch (e) {
        console.log(e)
        this.$snack.netWorkError()
      } finally {
        this.loading = false
      }
    },
    
    handleLiveViewStart() {
      // Remove existing listeners to prevent duplicates
      this.socket.off(`lv-${this.sessionId}`, this.handleLiveViewRun);
      this.socket.off(`lv-stream-${this.sessionId}`, this.handleLiveViewStream);
      
      // Join stream and add listeners
      this.socket.emit('joinStream', `lv-${this.sessionId}`);
      this.socket.on(`lv-${this.sessionId}`, this.handleLiveViewRun);
      this.socket.on(`lv-stream-${this.sessionId}`, this.handleLiveViewStream);
      
      // Start keep-alive interval to extend TTL
      this.startKeepAlive();
    },
    
    startKeepAlive() {
      // Clear any existing interval
      if (this.keepAliveInterval) {
        clearInterval(this.keepAliveInterval);
      }
      
      // Update TTL every 5 seconds
      this.keepAliveInterval = setInterval(() => {
        // Update TTL to now + 30 seconds
        this.tte = new Date(Date.now() + 30000).toISOString();
        
        // Send update to backend
        this.$newReq('POST', `devices/liveViewUpdate`, {
          deviceId: this.deviceId,
          sessionId: this.sessionId,
          options: {
            fps: this.fps,
            monitor: this.monitor,
            quality: this.quality,
            tte: this.tte,
          }
        }).catch(e => {
          console.log('Failed to update TTL:', e);
        });
      }, 5000);
    },
    
    handleLiveViewStream(data) {
      switch (data.action) {
        case 'count': {
          this.monitorCount = +data.count
          break;
        }
        default: {
          console.log('No Action found for', data)
        }
      }
    },
    
    handleLiveViewRun(data) {
      const {sessionId, image} = data
      this.imageData = image
      
    },
    
    async handleMonitorChange() {
      // Send monitor change request to backend
      console.log('Monitor changed to:', this.monitor);
      try {
        await this.$newReq('POST', `devices/liveViewUpdate`, {
          deviceId: this.deviceId,
          sessionId: this.sessionId,
          options: {
            fps: this.fps,
            monitor: this.monitor,
            quality: this.quality,
            tte: this.tte,
          }
        })
        console.log('Monitor update sent successfully');
      } catch (e) {
        console.log(e)
        this.$snack.netWorkError()
      }
    },
    
    async handleClose() {
      // Clear keep-alive interval
      if (this.keepAliveInterval) {
        clearInterval(this.keepAliveInterval);
        this.keepAliveInterval = null;
      }
      
      // Clean up socket listeners
      if (this.sessionId) {
        this.socket.off(`lv-${this.sessionId}`, this.handleLiveViewRun);
        this.socket.off(`lv-stream-${this.sessionId}`, this.handleLiveViewStream);
        this.socket.emit('leaveStream', `lv-${this.sessionId}`);
      }
      
      // Clear image data to free memory
      this.imageData = null;
      
      // Stop the live view session
      if (this.sessionId && this.deviceId) {
        try {
          await this.$newReq('POST', `devices/liveViewStop`, {sessionId: this.sessionId, deviceId: this.deviceId})
        } catch (e) {
          console.log(e)
          this.$snack.netWorkError()
        }
      }
      
      // Clear session data
      this.sessionId = null;
    },
    
    // View control methods
    zoomIn() {
      this.zoom = Math.min(this.zoom + 0.25, 3);
      this.fitMode = 'actual'; // Switch to actual size mode when zooming
    },
    
    zoomOut() {
      this.zoom = Math.max(this.zoom - 0.25, 0.5);
      if (this.zoom === 1) {
        this.fitMode = 'contain'; // Return to contain mode at 100%
        this.resetPan(); // Reset pan when returning to 100%
      }
    },
    
    rotate() {
      this.rotation = (this.rotation + 90) % 360;
      this.resetPan(); // Reset pan when rotating to avoid confusion
    },
    
    onImageLoad(event) {
      const img = event.target;
      this.imageDimensions = {
        width: img.naturalWidth,
        height: img.naturalHeight
      };
    },
    
    captureImage() {
      if (!this.imageData) {
        this.$snack.showError('No image to capture');
        return;
      }
      
      // Create a temporary link element
      const link = document.createElement('a');
      link.href = this.imageData;
      
      // Generate filename with employee name, hostname, and timestamp
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      const employeeName = (this.employee && this.employee.name && this.employee.name.replace(/\s+/g, '_')) || 'Unassigned';
      const hostname = (this.device && this.device.hostname) || 'Unknown';
      link.download = `LiveView_${employeeName}_${hostname}_${timestamp}.png`;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      this.$snack.showSuccess('Image captured');
    },
    
    // Pan/drag methods
    startPan(event) {
      // Only allow panning when zoomed in or in actual size mode
      if ((this.fitMode === 'actual' && this.zoom > 1) || this.fitMode === 'cover') {
        this.isPanning = true;
        this.panStart = {
          x: event.clientX - this.panOffset.x,
          y: event.clientY - this.panOffset.y
        };
        event.preventDefault();
      }
    },
    
    doPan(event) {
      if (this.isPanning) {
        this.panOffset = {
          x: event.clientX - this.panStart.x,
          y: event.clientY - this.panStart.y
        };
        event.preventDefault();
      }
    },
    
    endPan(event) {
      this.isPanning = false;
    },
    
    handleWheel(event) {
      // Allow zoom with mouse wheel when Ctrl is pressed
      if (event.ctrlKey) {
        event.preventDefault();
        if (event.deltaY < 0) {
          this.zoomIn();
        } else {
          this.zoomOut();
        }
      }
    },
    
    resetPan() {
      this.panOffset = { x: 0, y: 0 };
      this.isPanning = false;
    }
    
  },
  computed: {
    ...mapGetters(['devicesRef', 'employeesRef']),
    
    device() {
      return this.devicesRef[this.deviceId]
    },
    
    employee() {
      return this.employeesRef[this.device?.employeeId]
    },
    
    shownRelay: {
      get() {
        return !!this.deviceId
      },
      set(val) {this.$emit('update:deviceId', null)}
    },
    monitorItems() {
      const items = [
        { text: 'All Monitors', value: null }
      ];
      
      // Add individual monitors based on monitorCount
      for (let i = 0; i < this.monitorCount; i++) {
        items.push({
          text: `Monitor ${i + 1}`,
          value: i + 1
        });
      }
      
      return items;
    },
    
    imageStyle() {
      const styles = {};
      
      if (this.fitMode === 'contain') {
        styles.maxWidth = '100%';
        styles.maxHeight = '100%';
        styles.width = 'auto';
        styles.height = 'auto';
      } else if (this.fitMode === 'cover') {
        styles.width = '100%';
        styles.height = '100%';
        styles.objectFit = 'cover';
      } else if (this.fitMode === 'actual') {
        styles.width = 'auto';
        styles.height = 'auto';
        styles.transform = `translate(${this.panOffset.x}px, ${this.panOffset.y}px) scale(${this.zoom}) rotate(${this.rotation}deg)`;
        styles.transformOrigin = 'center';
      }
      
      if (this.fitMode !== 'actual') {
        styles.transform = `translate(${this.panOffset.x}px, ${this.panOffset.y}px) rotate(${this.rotation}deg)`;
        styles.transformOrigin = 'center';
      }
      
      styles.display = 'block';
      // Only animate if not panning
      if (!this.isPanning) {
        styles.transition = 'transform 0.3s ease';
      }
      
      // Make image unselectable
      styles.userSelect = 'none';
      styles.pointerEvents = 'none';
      
      return styles;
    },
    
    imageWrapperStyle() {
      const styles = {};
      
      if (this.fitMode === 'actual' && this.zoom !== 1) {
        // Add padding to accommodate zoomed image
        const padding = Math.max(0, (this.zoom - 1) * 50);
        styles.padding = `${padding}% ${padding}%`;
      }
      
      if (this.fitMode === 'contain' || this.fitMode === 'cover') {
        styles.width = '100%';
        styles.height = '100%';
        styles.display = 'flex';
        styles.alignItems = 'center';
        styles.justifyContent = 'center';
      }
      
      return styles;
    },
    
    containerStyle() {
      const styles = {};
      
      // Show grab cursor when image is pannable
      if ((this.fitMode === 'actual' && this.zoom > 1) || this.fitMode === 'cover') {
        styles.cursor = this.isPanning ? 'grabbing' : 'grab';
      } else {
        styles.cursor = 'default';
      }
      
      return styles;
    }
  },
}
</script>

<style scoped>

</style>